import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { pucAPI, vehicleAPI } from '../../api/index';
import { queryClient } from '../../App';
import { PUC, Vehicle } from '../../types/index';
import { Button } from '../Button';

interface AddPUCModalProps {
  open: boolean
  onClose: () => void
  customerId: string
  puc?: PUC
  onSuccess?: () => void
}

type PUCResult = 'pass' | 'fail'

const pucResults: PUCResult[] = ['pass', 'fail']

export default function AddPUCModal({
  open,
  onClose,
  customerId,
  puc,
  onSuccess,
}: AddPUCModalProps) {
  const [formData, setFormData] = useState({
    vehicle_id: '',
    certificate_number: '',
    test_date: '',
    expiry_date: '',
    test_center: '',
    test_center_address: '',
    co_level: '',
    hc_level: '',
    result: '' as PUCResult | '',
  })

  // Fetch vehicles for the customer
  const { data: vehiclesData, isLoading: isLoadingVehicles } = useQuery({
    queryKey: ['vehicles', customerId],
    queryFn: () => vehicleAPI.listForCustomer(customerId),
    enabled: open,
  })

  useEffect(() => {
    if (puc) {
      setFormData({
        vehicle_id: puc.vehicle_id || '',
        certificate_number: puc.certificate_number || '',
        test_date: puc.test_date || '',
        expiry_date: puc.expiry_date || '',
        test_center: puc.test_center || '',
        test_center_address: puc.test_center_address || '',
        co_level: puc.co_level || '',
        hc_level: puc.hc_level || '',
        result: puc.result || '',
      })
    } else {
      setFormData({
        vehicle_id: '',
        certificate_number: '',
        test_date: '',
        expiry_date: '',
        test_center: '',
        test_center_address: '',
        co_level: '',
        hc_level: '',
        result: '',
      })
    }
  }, [puc, open])

  const createMutation = useMutation({
    mutationFn: () => {
      const payload = {
        vehicle_id: formData.vehicle_id,
        certificate_number: formData.certificate_number,
        test_date: formData.test_date || undefined,
        expiry_date: formData.expiry_date || undefined,
        test_center: formData.test_center || undefined,
        test_center_address: formData.test_center_address || undefined,
        co_level: formData.co_level ? parseFloat(formData.co_level) : undefined,
        hc_level: formData.hc_level ? parseFloat(formData.hc_level) : undefined,
        result: formData.result || undefined,
      } as any
      return pucAPI.create(customerId, payload)
    },
    onSuccess: () => {
      toast.success('PUC created')
      queryClient.invalidateQueries({ queryKey: ['pucs', customerId] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to create PUC')
    },
  })

  const updateMutation = useMutation({
    mutationFn: () => {
      const payload = {
        vehicle_id: formData.vehicle_id,
        certificate_number: formData.certificate_number,
        test_date: formData.test_date || undefined,
        expiry_date: formData.expiry_date || undefined,
        test_center: formData.test_center || undefined,
        test_center_address: formData.test_center_address || undefined,
        co_level: formData.co_level ? parseFloat(formData.co_level) : undefined,
        hc_level: formData.hc_level ? parseFloat(formData.hc_level) : undefined,
        result: formData.result || undefined,
      } as any
      return pucAPI.update(puc!.id, payload)
    },
    onSuccess: () => {
      toast.success('PUC updated')
      queryClient.invalidateQueries({ queryKey: ['pucs', customerId] })
      queryClient.invalidateQueries({ queryKey: ['puc', puc!.id] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to update PUC')
    },
  })

  const isLoading =
    createMutation.isPending || updateMutation.isPending || isLoadingVehicles
  const isEdit = !!puc

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !formData.vehicle_id.trim() ||
      !formData.certificate_number.trim() ||
      !formData.test_date ||
      !formData.expiry_date
    ) {
      toast.error('Vehicle, certificate number, test date, and expiry date are required')
      return
    }
    if (isEdit) {
      updateMutation.mutate()
    } else {
      createMutation.mutate()
    }
  }

  if (!open) return null

  const vehicles = vehiclesData?.data?.vehicles || []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit PUC' : 'Add PUC'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vehicle *
              </label>
              {isEdit && puc?.vehicle_reg ? (
                <div className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 bg-gray-100 text-gray-700">
                  {puc.vehicle_reg}
                </div>
              ) : (
                <select
                  required
                  disabled={isLoading}
                  value={formData.vehicle_id}
                  onChange={(e) =>
                    setFormData({ ...formData, vehicle_id: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                >
                  <option value="">Select vehicle</option>
                  {Array.isArray(vehicles) && vehicles.map((vehicle: Vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.registration_number} - {vehicle.make} {vehicle.model}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Certificate Number *
              </label>
              <input
                type="text"
                required
                disabled={isLoading}
                value={formData.certificate_number}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    certificate_number: e.target.value,
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="PUC-2024-001"
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Test Date *
              </label>
              <input
                type="date"
                required
                disabled={isLoading}
                value={formData.test_date}
                onChange={(e) =>
                  setFormData({ ...formData, test_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Expiry Date *
              </label>
              <input
                type="date"
                required
                disabled={isLoading}
                value={formData.expiry_date}
                onChange={(e) =>
                  setFormData({ ...formData, expiry_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Test Center
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.test_center}
                onChange={(e) =>
                  setFormData({ ...formData, test_center: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Test Center Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Test Center Address
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.test_center_address}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    test_center_address: e.target.value,
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Address"
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                CO Level
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.co_level}
                onChange={(e) =>
                  setFormData({ ...formData, co_level: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="e.g., 0.05%"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                HC Level
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.hc_level}
                onChange={(e) =>
                  setFormData({ ...formData, hc_level: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="e.g., 100 ppm"
              />
            </div>
          </div>

          {/* Row 5 */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Result
            </label>
            <select
              disabled={isLoading}
              value={formData.result}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  result: e.target.value as PUCResult | '',
                })
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
            >
              <option value="">Select result</option>
              {pucResults.map((result) => (
                <option key={result} value={result}>
                  {result.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-4 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              variant="primary"
            >
              {isEdit ? 'Update PUC' : 'Add PUC'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
